import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const pickType = () => ["audio/webm", "audio/mp4", "audio/ogg"].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export const VoiceButton = ({ uid, onText, disabled, testid = "voice-note-button" }) => {
  const [state, setState] = useState("idle");
  const [secs, setSecs] = useState(0);
  const rec = useRef(null);

  useEffect(() => {
    if (state !== "recording") return undefined;
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [state]);
  useEffect(() => () => rec.current?.stream?.getTracks().forEach((t) => t.stop()), []);

  const send = async (blob) => {
    setState("busy");
    try {
      const fd = new FormData();
      fd.append("file", blob, `note.${blob.type.includes("mp4") ? "mp4" : "webm"}`);
      const { data } = await api.post(`/universes/${uid}/ai/transcribe`, fd);
      if (data.text) { onText(data.text); toast.success("Voice note transcribed"); } else toast.error("Couldn't hear any words. Try again.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setState("idle");
    }
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return toast.error("Voice recording isn't supported in this browser.");
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      const msg = { NotAllowedError: "Microphone access was blocked. Allow it in your browser settings.", NotFoundError: "No microphone was found on this device.", NotReadableError: "Your microphone is in use by another app." }[e?.name];
      return toast.error(msg || `Couldn't start the microphone (${e?.name || "error"}).`);
    }
    try {
      const type = pickType();
      const r = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      const chunks = [];
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = () => { stream.getTracks().forEach((t) => t.stop()); send(new Blob(chunks, { type: r.mimeType || type || "audio/webm" })); };
      rec.current = r;
      r.start();
      setSecs(0);
      setState("recording");
    } catch (e) {
      stream.getTracks().forEach((t) => t.stop());
      toast.error(`Recording isn't available in this browser (${e?.name || "error"}).`);
    }
  };

  const stop = () => rec.current?.state === "recording" && rec.current.stop();

  const recording = state === "recording";
  return (
    <button type="button" onClick={recording ? stop : start} disabled={disabled || state === "busy"} data-testid={testid}
      aria-label={recording ? "Stop recording" : "Dictate a note"}
      className={`inline-flex h-10 items-center gap-1.5 rounded-md border px-3 text-sm transition-colors disabled:opacity-50 ${recording ? "border-[#B91C1C] bg-[#B91C1C]/15 text-[#FCA5A5]" : "border-[#2D3442] text-[#E8DFD1] hover:border-[#D97706]/60 hover:text-[#F59E0B]"}`}>
      {state === "busy" ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-3.5 w-3.5 fill-current" /> : <Mic className="h-4 w-4" />}
      <span data-testid={`${testid}-label`}>{state === "busy" ? "Transcribing…" : recording ? `Stop ${fmt(secs)}` : "Dictate"}</span>
    </button>
  );
};

export const appendText = (prev, add) => (prev.trim() ? `${prev.replace(/\s+$/, "")}\n\n${add}` : add);
